import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface MarketMetrics {
  asset: string;
  price: number;
  px24h: number;
  oi24h: number;
  funding24h: number;
  topTraderRatio: number;
  liquidations24h: number;
  liquidations24hCount: number;
  timestamp: string;
  source: string;
  last_success_at?: string;
}

export const useMarketMetrics = (asset: 'BTC' | 'ETH') => {
  return useQuery({
    queryKey: ['market-metrics', asset],
    queryFn: async (): Promise<MarketMetrics> => {
      // Trigger collection (fire-and-forget)
      supabase.functions.invoke('collect-market-metrics', {}).catch(() => {});
      
      // Read from new cache table
      const { data, error } = await supabase
        .from('market_metrics_cache')
        .select('data, source')
        .eq('id', `market_metrics_${asset}`)
        .maybeSingle();
      
      if (error || !data?.data) {
        throw new Error(`Failed to fetch ${asset} metrics`);
      }
      
      const metrics = data.data as any;
      
      return {
        asset,
        price: metrics.price ?? 0,
        px24h: metrics.px_24h ?? 0,
        oi24h: Number(metrics.oi_change_24h ?? 0),
        // funding24h stored as decimal (e.g., 0.00018), convert to % for display
        funding24h: Number(metrics.funding_sum_24h ?? 0) * 100,
        topTraderRatio: Number(metrics.ls_ratio_avg_24h ?? 0),
        liquidations24h: Number(metrics.liquidations_24h ?? 0),
        liquidations24hCount: Number(metrics.liquidations_24h_count ?? 0),
        timestamp: metrics.updated_at,
        source: data.source,
      } as MarketMetrics;
    },
    refetchInterval: 5 * 60000,
    staleTime: 2 * 60000,
  });
};
